import React from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './CourseDetails.module.css';
import EnrollmentForm from '../EnrollmentForm/EnrollmentForm';
import Ai from '../../assets/AI.jpg';
import Cybersecurity from '../../assets/CyberSecurity.jpg';
import Gamedev from '../../assets/GameDev.jpg';
import Fullstack from '../../assets/Fullstack.jpg';

const coursesData = {
  1: {
    title: 'Cybersecurity Professional',
    description: 'Become an expert in securing systems and networks against digital attacks',
    duration: '12 Weeks',
    level: 'Advanced',
    instructor: 'Dr. Sarah Chen',
    instructorBio: '10+ years in penetration testing, former Chief Security Officer at TechDefense',
    image: Cybersecurity,
    curriculum: [
      {
        week: 'Week 1-3',
        title: 'Fundamentals of Cybersecurity',
        topics: [
          'Introduction to cyber threats',
          'Cryptography basics',
          'Network security protocols'
        ]
      },
      {
        week: 'Week 4-6',
        title: 'Ethical Hacking',
        topics: [
          'Penetration testing methodologies',
          'Vulnerability assessment',
          'OWASP Top 10'
        ]
      },
      {
        week: 'Week 7-9',
        title: 'Defensive Strategies',
        topics: [
          'Firewall configuration',
          'Intrusion detection systems',
          'Incident response'
        ]
      },
      {
        week: 'Week 10-12',
        title: 'Capstone Project',
        topics: [
          'Real-world simulation',
          'Security audit',
          'Final presentation'
        ]
      }
    ]
  },
   2: {
    title: 'Artificial Intelligence and Machine Learning',
    description: 'Become an expert in securing systems and networks against digital attacks',
    duration: '12 Weeks',
    level: 'Advanced',
    instructor: 'Dr. Sarah Chen',
    instructorBio: '10+ years in penetration testing, former Chief Security Officer at TechDefense',
    image: Ai,
    curriculum: [
      {
        week: 'Week 1-3',
        title: 'Fundamentals of Cybersecurity',
        topics: [
          'Introduction to cyber threats',
          'Cryptography basics',
          'Network security protocols'
        ]
      },
      {
        week: 'Week 4-6',
        title: 'Ethical Hacking',
        topics: [
          'Penetration testing methodologies',
          'Vulnerability assessment',
          'OWASP Top 10'
        ]
      },
      {
        week: 'Week 7-9',
        title: 'Defensive Strategies',
        topics: [
          'Firewall configuration',
          'Intrusion detection systems',
          'Incident response'
        ]
      },
      {
        week: 'Week 10-12',
        title: 'Capstone Project',
        topics: [
          'Real-world simulation',
          'Security audit',
          'Final presentation'
        ]
      }
    ]
  },
   3: {
    title: 'Game Development',
    description: 'Become an expert in securing systems and networks against digital attacks',
    duration: '12 Weeks',
    level: 'Advanced',
    instructor: 'Dr. Sarah Chen',
    instructorBio: '10+ years in penetration testing, former Chief Security Officer at TechDefense',
    image: Gamedev,
    curriculum: [
      {
        week: 'Week 1-3',
        title: 'Fundamentals of Cybersecurity',
        topics: [
          'Introduction to cyber threats',
          'Cryptography basics',
          'Network security protocols'
        ]
      },
      {
        week: 'Week 4-6',
        title: 'Ethical Hacking',
        topics: [
          'Penetration testing methodologies',
          'Vulnerability assessment',
          'OWASP Top 10'
        ]
      },
      {
        week: 'Week 7-9',
        title: 'Defensive Strategies',
        topics: [
          'Firewall configuration',
          'Intrusion detection systems',
          'Incident response'
        ]
      },
      {
        week: 'Week 10-12',
        title: 'Capstone Project',
        topics: [
          'Real-world simulation',
          'Security audit',
          'Final presentation'
        ]
      }
    ]
  },
   4: {
    title: 'Full Stack Development',
    description: 'Become an expert in securing systems and networks against digital attacks',
    duration: '12 Weeks',
    level: 'Advanced',
    instructor: 'Dr. Sarah Chen',
    instructorBio: '10+ years in penetration testing, former Chief Security Officer at TechDefense',
    image: Fullstack,
    curriculum: [
      {
        week: 'Week 1-3',
        title: 'Fundamentals of Cybersecurity',
        topics: [
          'Introduction to cyber threats',
          'Cryptography basics',
          'Network security protocols'
        ]
      },
      {
        week: 'Week 4-6',
        title: 'Ethical Hacking',
        topics: [
          'Penetration testing methodologies',
          'Vulnerability assessment',
          'OWASP Top 10'
        ]
      },
      {
        week: 'Week 7-9',
        title: 'Defensive Strategies',
        topics: [
          'Firewall configuration',
          'Intrusion detection systems',
          'Incident response'
        ]
      },
      {
        week: 'Week 10-12',
        title: 'Capstone Project',
        topics: [
          'Real-world simulation',
          'Security audit',
          'Final presentation'
        ]
      }
    ]
  }
};

const CourseDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const course = coursesData[id as unknown as keyof typeof coursesData];

  if (!course) {
    return <div>Course not found</div>;
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles.courseDetails}
    >
      {/* Hero Section */}
      <section className={styles.hero}>
        <motion.div 
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className={styles.heroContent}
        >
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            ← Back to Courses
          </button>
          <h1>{course.title}</h1>
          <p className={styles.meta}>
            <span>{course.duration}</span>
            <span>{course.level}</span>
          </p>
          <p className={styles.description}>{course.description}</p>
          <button className={styles.enrollButton}>
            Enroll Now
          </button>
        </motion.div>
        <motion.div 
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={styles.heroImage}
        >
          <img src={course.image} alt={course.title} />
        </motion.div>
      </section>

      {/* Course Content */}
      <motion.section 
        initial={{ y: 50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
        className={styles.curriculum}
      >
        <h2>Course <span>Curriculum</span></h2>
        <div className={styles.timeline}>
          {course.curriculum.map((item, index) => (
            <motion.div 
              key={index}
              initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className={styles.timelineItem}
            >
              <div className={styles.timelineMarker}></div>
              <div className={styles.timelineContent}>
                <h3>{item.week}: {item.title}</h3>
                <ul>
                  {item.topics.map((topic, i) => (
                    <li key={i}>{topic}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Instructor Section */}
      <motion.section 
        initial={{ y: 50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true }}
        className={styles.instructor}
      >
        <h2>Your <span>Instructor</span></h2>
        <div className={styles.instructorCard}>
          <div className={styles.instructorImage}>
            <div className={styles.imagePlaceholder}></div>
          </div>
          <div className={styles.instructorInfo}>
            <h3>{course.instructor}</h3>
            <p className={styles.instructorTitle}>Lead Cybersecurity Expert</p>
            <p className={styles.instructorBio}>{course.instructorBio}</p>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        initial={{ scale: 0.95, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        className={styles.cta}
      >
        <h2>Ready to Start Your Journey?</h2>
        <p>Join {course.title} today and gain in-demand skills</p>
        <EnrollmentForm courseTitle={course.title} />
      </motion.section>
    </motion.div>
  );
};

export default CourseDetails;